import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Play } from "lucide-react";
import { WordReveal, DrawLine, reducedMotion } from "@/components/motion";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 44 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.68, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Chapter = ({ num, title, sub }) => (
  <div className="mb-14 -mx-2 px-2 py-4">
    <Reveal>
      <div className="flex items-end gap-5">
        <span className="font-display text-6xl leading-none font-bold text-crimson/25 select-none md:text-7xl">
          {num}
        </span>
        <div>
          <h2 className="font-display text-3xl font-semibold sm:text-4xl">
            <motion.span
              className="inline-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              {title}
            </motion.span>
          </h2>
          {sub && <p className="mt-2 text-sm text-white/65 md:text-base">{sub}</p>}
        </div>
      </div>
    </Reveal>
    <DrawLine className="mt-6 w-full" />
  </div>
);

export const CountUp = ({ value, className = "", active, duration = 2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const go = active !== undefined ? active : inView;
  const str = String(value ?? "");
  const m = str.match(/([\d.,]+)([\s\S]*)/);
  const target = m ? parseFloat(m[1].replace(/,/g, "")) : 0;
  const suffix = m ? m[2] : "";
  const decimals = m && m[1].includes(".") ? m[1].split(".")[1].replace(/,/g, "").length : 0;
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!go || !m) return;
    const fmt = (v) =>
      v.toLocaleString("en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    if (reducedMotion()) {
      setDisplay(fmt(target));
      return;
    }
    const controls = animate(0, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(fmt(v)),
    });
    return () => controls.stop();
  }, [go, target, decimals, duration]);

  if (!m) return <span className={className}>{str}</span>;
  return (
    <span ref={ref} className={`select-none ${className}`}>
      {display}
      {suffix}
    </span>
  );
};

export const youtubeId = (url) => {
  if (!url) return null;
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

export const YoutubeEmbed = ({ url, title, testId }) => {
  const id = youtubeId(url);
  const [play, setPlay] = useState(false);
  if (!id) {
    return (
      <div
        data-testid={testId}
        className="flex aspect-video flex-col items-center justify-center gap-3 rounded-lg border border-white/10 bg-surface p-6 text-center"
      >
        <span className="h-px w-10 bg-crimson" />
        <p className="text-sm leading-snug text-white/75">{title}</p>
        <p className="text-[11px] tracking-widest text-white/30 uppercase">Video link coming soon</p>
      </div>
    );
  }
  // lightweight facade: the heavy YouTube iframe loads only on click
  if (!play) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={() => setPlay(true)}
        aria-label={`Play ${title}`}
        className="group relative block aspect-video w-full cursor-pointer overflow-hidden rounded-lg border border-white/10 bg-black text-left"
      >
        <img
          src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
          alt={title}
          width="480"
          height="360"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover opacity-80 transition-[opacity,transform] duration-500 group-hover:scale-[1.03] group-hover:opacity-100"
        />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-crimson text-white shadow-2xl transition-transform duration-300 group-hover:scale-110">
            <Play size={22} className="ml-1" fill="currentColor" />
          </span>
        </span>
      </button>
    );
  }
  return (
    <div data-testid={testId} className="aspect-video overflow-hidden rounded-lg border border-white/10 bg-black">
      <iframe
        className="h-full w-full"
        src={`https://www.youtube.com/embed/${id}?autoplay=1`}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

export const instaId = (url) => {
  if (!url) return null;
  const m = String(url).match(/instagram\.com\/(reel|reels|p)\/([\w-]+)/);
  return m ? m[2] : null;
};

export const InstaEmbed = ({ url, views, testId, badgeClassName = "" }) => {
  const id = instaId(url);
  const badge = views ? (
    <div className={`mt-4 flex justify-center ${badgeClassName}`}>
      <span className="inline-flex items-center gap-2.5 rounded-full border border-crimson/60 bg-crimson/20 px-6 py-2.5 text-sm font-extrabold tracking-wide text-white shadow-[0_0_26px_rgba(211,47,47,0.35)]">
        <Play size={14} className="fill-crimson text-crimson" />
        {views} <span className="text-crimson">views</span>
      </span>
    </div>
  ) : null;
  if (!id) {
    return (
      <div>
        <div
          data-testid={testId}
          className="flex aspect-[9/14] flex-col items-center justify-center gap-3 rounded-lg border border-white/10 bg-surface p-6 text-center"
        >
          <span className="h-px w-10 bg-crimson" />
          <p className="text-xs tracking-widest text-white/30 uppercase">Reel link coming soon</p>
        </div>
        {badge}
      </div>
    );
  }
  return (
    <div>
      <div data-testid={testId} className="overflow-hidden rounded-lg border border-white/10 bg-black">
        <iframe
          className="aspect-[9/14] w-full"
          src={`https://www.instagram.com/reel/${id}/embed`}
          title="Instagram reel"
          loading="lazy"
          allow="encrypted-media"
        />
      </div>
      {badge}
    </div>
  );
};

export const GoldButton = ({ children, href, onClick, testId, className = "" }) => {
  const cls = `inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-bold tracking-wide text-black transition-all duration-300 select-none hover:shadow-[0_0_34px_rgba(212,175,55,0.45)] hover:brightness-110 ${className}`;
  if (href)
    return (
      <a href={href} target={href.startsWith("#") ? undefined : "_blank"} rel="noopener noreferrer" data-testid={testId} className={cls} onClick={onClick}>
        {children}
      </a>
    );
  return (
    <button type="button" data-testid={testId} className={cls} onClick={onClick}>
      {children}
    </button>
  );
};

export const RedButton = ({ children, href, onClick, testId, className = "" }) => {
  const cls = `inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-crimson to-blood px-7 py-3 text-sm font-bold tracking-wide text-white transition-all duration-300 select-none hover:box-glow-red-strong ${className}`;
  if (href)
    return (
      <a href={href} target={href.startsWith("#") ? undefined : "_blank"} rel="noopener noreferrer" data-testid={testId} className={cls} onClick={onClick}>
        {children}
      </a>
    );
  return (
    <button type="button" data-testid={testId} className={cls} onClick={onClick}>
      {children}
    </button>
  );
};

// right-side hero index of a page's numbered sections - desktop only (>=900px)
export const SectionIndex = ({ items }) => (
  <nav
    data-hero
    data-testid="section-index"
    className="hidden w-full max-w-[320px] justify-self-end self-center select-none min-[900px]:block"
  >
    {items.map((it, i) => (
      <a
        key={it.id}
        href={`#${it.id}`}
        data-testid={`section-index-${it.id}`}
        onClick={(e) => {
          e.preventDefault();
          const el = document.getElementById(it.id);
          if (!el) return;
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64 });
          else el.scrollIntoView({ behavior: "smooth" });
        }}
        className={`group flex h-11 items-center gap-3 ${i > 0 ? "border-t border-[rgba(255,255,255,0.06)]" : ""}`}
      >
        <span className="font-display text-sm font-bold text-crimson/40 transition-colors duration-300 group-hover:text-gold">
          {it.num}
        </span>
        <span className="text-[15px] text-white/75 transition-colors duration-300 group-hover:text-white">
          {it.label}
        </span>
      </a>
    ))}
  </nav>
);
